import type { Router } from 'vue-router'

import { i18n } from '@/lib/i18n'
import { isStaff, landingRoute } from '@/lib/permissions'
import { initPreline } from '@/lib/preline'
import { useAuthStore } from '@/stores/auth'
import { can } from '@/lib/permissions'

/**
 * Route guards are a convenience: they keep users out of screens that would
 * only show them errors. The actual boundary is the API.
 *
 * Every check fails closed. While the session is still resolving we await it
 * rather than guessing, so a slow network cannot flash admin screens.
 */
export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    if (!auth.initialized) {
      await auth.initialize()
    }

    if (to.meta.public) {
      if (to.name === 'landing' && auth.isAuthenticated) {
        return { name: 'dashboard' }
      }
      if (to.meta.guestOnly && auth.isAuthenticated) {
        return landingRoute(auth.role)
      }
      return true
    }

    if (!auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // A deactivated account keeps its token until it expires, so the check has
    // to happen here as well as in the database.
    if (!auth.isActive) {
      await auth.signOut()
      return { name: 'login', query: { reason: 'deactivated' } }
    }

    if (to.meta.staffOnly && !isStaff(auth.role)) {
      return { name: 'forbidden' }
    }

    if (to.meta.capability && !can(auth.role, to.meta.capability)) {
      return { name: 'forbidden' }
    }

    return true
  })

  router.afterEach((to) => {
    const base = i18n.global.t('common.appName')
    const titleKey = to.meta.titleKey
    const page = titleKey ? i18n.global.t(titleKey) : ''
    document.title = page && page !== base ? `${page} · ${base}` : base

    // Preline's plugins bind to the DOM once. The SPA replaces that DOM on
    // every navigation, so they have to be re-attached afterwards.
    requestAnimationFrame(() => initPreline())
  })
}
